import { useMemo } from 'react'
import { styled } from '@mui/material/styles'
import Modal from '@mui/material/Modal'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { useParams, useRouteLoaderData, useNavigate, useLoaderData } from 'react-router-dom'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { useTranslation } from 'react-i18next'

import { LoaderData } from '../../../types'
import type { eventLoader } from '../Events.loader'
import type { eventCheckin } from './CheckinEvent.loader'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  maxWidth: '70rem',
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

const Title = styled('h2')(() => ({
  marginBottom: 0,
}))

export default function CheckinEvent() {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const { eventId, subEventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const data = useRouteLoaderData('view-event') as LoaderData<ReturnType<typeof eventLoader>>

  const loaderData = useLoaderData() as LoaderData<ReturnType<typeof eventCheckin>>

  const subEvent = useMemo(() => {
    if (subEventId) {
      return data.subEvents.find(el => el.id === Number(subEventId))
    }
    return null
  }, [data.subEvents, subEventId])

  const handleClose = () => {
    navigate(`/events/${eventId}`)
  }

  return (
    <Modal open onClose={handleClose}>
      <Stack sx={style}>
        <Stack>
          <Stack direction="row" justifyContent="space-between">
            <Title>
              {subEventId ? "Présence à l'intervention" : t("Présence à l'événement")} :{' '}
              {data.event?.name} {subEvent ? ` - ${subEvent?.name}` : ''}
            </Title>
          </Stack>
          <Divider />
        </Stack>

        <Stack flex="1 0 auto" alignItems="center" justifyContent="center">
          {data.event && (subEventId ? subEvent : true) ? (
            <>
              <div style={{ position: 'relative' }}>
                <ConfirmationNumberIcon sx={{ fontSize: '10rem' }} />
                {!!(loaderData.error as FetchBaseQueryError) ? (
                  <CancelIcon
                    sx={{
                      fontSize: '4rem',
                      color: 'error.main',
                      position: 'absolute',
                      right: '-10%',
                      top: '0',
                    }}
                  />
                ) : (
                  <CheckCircleIcon
                    sx={{
                      fontSize: '4rem',
                      color: 'success.main',
                      position: 'absolute',
                      right: '0',
                      top: '0',
                    }}
                  />
                )}
              </div>
              <h5>
                {!!(loaderData.error as FetchBaseQueryError)
                  ? t('Votre présence ne peut être confirmée')
                  : t('Votre présence est confirmée')}
                .
              </h5>
              {t('Vous pouvez maintenant fermer cette fenêtre.')}
            </>
          ) : (
            <h5>{t("Cet événement n'existe pas")}</h5>
          )}
        </Stack>

        <Stack
          direction="row"
          justifyContent="flex-end"
          paddingBottom="1.5rem"
          sx={{
            position: 'sticky',
            bottom: '-1rem',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <Button
            onClick={e => {
              e.preventDefault()
              handleClose()
            }}
            sx={{ pointerEvents: 'all' }}
          >
            {t('Fermer')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  )
}
